import { recordIsUnconfirmedEntryId } from "../contracts/business-graph-entry-state.js";
/** Declarative fact paths required before a site package may be generated or approved. */
export const BUSINESS_GRAPH_PACKAGE_FACT_PATH = {
    BUSINESS_NAME: "business.businessName",
    CONTACT_METHOD: "business.contactMethod",
    CONFIRMED_PRIMARY_SERVICE: "services.confirmedPrimary",
    CONFIRMED_SERVICE_AREA: "operations.confirmedServiceArea",
    CONFIRMED_SERVICES_MIN_2: "services.confirmedMinCount:2",
    CONFIRMED_PROOF: "trust.confirmedProof",
    CONFIRMED_MEDIA: "assets.confirmedMedia",
};
/**
 * Per-package required-field contract (BG-006).
 * Richness score thresholds remain secondary; these paths hard-block first.
 */
export const SITE_PACKAGE_REQUIRED_FACT_PATHS = {
    "package-one-page": [
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.BUSINESS_NAME,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONTACT_METHOD,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PRIMARY_SERVICE,
    ],
    "package-basic-3": [
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.BUSINESS_NAME,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONTACT_METHOD,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PRIMARY_SERVICE,
    ],
    "package-standard-5": [
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.BUSINESS_NAME,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONTACT_METHOD,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PRIMARY_SERVICE,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICE_AREA,
    ],
    "package-growth-8": [
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.BUSINESS_NAME,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONTACT_METHOD,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PRIMARY_SERVICE,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICE_AREA,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICES_MIN_2,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PROOF,
    ],
    "package-authority-10": [
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.BUSINESS_NAME,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONTACT_METHOD,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PRIMARY_SERVICE,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICE_AREA,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICES_MIN_2,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PROOF,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_MEDIA,
    ],
    "package-authority-15": [
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.BUSINESS_NAME,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONTACT_METHOD,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PRIMARY_SERVICE,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICE_AREA,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICES_MIN_2,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PROOF,
        BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_MEDIA,
    ],
};
function hasContactMethod(graph) {
    return Boolean(graph.business.primaryPhone?.trim() || graph.business.primaryEmail?.trim());
}
function confirmedServices(graph) {
    return graph.services.filter((s) => s.name.trim().length > 0 && !recordIsUnconfirmedEntryId(s.id));
}
function hasConfirmedPrimaryService(graph) {
    return confirmedServices(graph).length >= 1;
}
function hasConfirmedServiceArea(graph) {
    return graph.operations.serviceAreas.some((a) => a.name.trim().length > 0 && a.id != null && !recordIsUnconfirmedEntryId(a.id));
}
function hasUnverifiedPrimaryService(graph) {
    return graph.services.some((s) => s.name.trim().length > 0 && recordIsUnconfirmedEntryId(s.id));
}
function hasUnverifiedServiceArea(graph) {
    return graph.operations.serviceAreas.some((a) => a.name.trim().length > 0 && a.id != null && recordIsUnconfirmedEntryId(a.id));
}
function hasConfirmedProof(graph) {
    const confirmedLicense = graph.trust.license.some((l) => !recordIsUnconfirmedEntryId(l.id));
    const confirmedInsurance = graph.trust.insurance.some((i) => !recordIsUnconfirmedEntryId(i.id));
    const confirmedCerts = graph.trust.certifications.some((c) => !recordIsUnconfirmedEntryId(c.id));
    const confirmedEvidence = graph.evidence.records.some((r) => !recordIsUnconfirmedEntryId(r.id) && r.evidenceType.trim().length > 0);
    return confirmedLicense || confirmedInsurance || confirmedCerts || confirmedEvidence;
}
function hasUnverifiedProofCandidates(graph) {
    return (graph.trust.license.some((l) => recordIsUnconfirmedEntryId(l.id)) ||
        graph.trust.insurance.some((i) => recordIsUnconfirmedEntryId(i.id)) ||
        graph.trust.certifications.some((c) => recordIsUnconfirmedEntryId(c.id)) ||
        graph.evidence.records.some((r) => recordIsUnconfirmedEntryId(r.id)));
}
function hasConfirmedMedia(graph) {
    return graph.assets.some((a) => !recordIsUnconfirmedEntryId(a.id));
}
function hasUnverifiedMedia(graph) {
    return graph.assets.some((a) => recordIsUnconfirmedEntryId(a.id));
}
function evaluateFactPath(graph, factPath) {
    switch (factPath) {
        case BUSINESS_GRAPH_PACKAGE_FACT_PATH.BUSINESS_NAME:
            if (!graph.business.businessName.trim()) {
                return {
                    factPath,
                    reason: "missing",
                    message: "Business name is required",
                };
            }
            return null;
        case BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONTACT_METHOD:
            if (!hasContactMethod(graph)) {
                return {
                    factPath,
                    reason: "missing",
                    message: "Contact method (phone or email) is required",
                };
            }
            return null;
        case BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PRIMARY_SERVICE:
            if (hasConfirmedPrimaryService(graph)) {
                return null;
            }
            if (hasUnverifiedPrimaryService(graph)) {
                return {
                    factPath,
                    reason: "unverified",
                    message: "Primary service must be agency-confirmed (unverified intake/import service)",
                };
            }
            return {
                factPath,
                reason: "missing",
                message: "At least one confirmed primary service is required",
            };
        case BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICE_AREA:
            if (hasConfirmedServiceArea(graph)) {
                return null;
            }
            if (hasUnverifiedServiceArea(graph)) {
                return {
                    factPath,
                    reason: "unverified",
                    message: "Service area must be agency-confirmed (unverified intake/import area)",
                };
            }
            return {
                factPath,
                reason: "missing",
                message: "Confirmed service area is required",
            };
        case BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_SERVICES_MIN_2: {
            const confirmed = confirmedServices(graph);
            if (confirmed.length >= 2) {
                return null;
            }
            const unverifiedNamed = graph.services.filter((s) => s.name.trim().length > 0 && recordIsUnconfirmedEntryId(s.id));
            if (unverifiedNamed.length > 0 && confirmed.length < 2) {
                return {
                    factPath,
                    reason: "unverified",
                    message: "At least two agency-confirmed services are required",
                };
            }
            return {
                factPath,
                reason: "missing",
                message: "At least two confirmed services are required",
            };
        }
        case BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_PROOF:
            if (hasConfirmedProof(graph)) {
                return null;
            }
            if (hasUnverifiedProofCandidates(graph)) {
                return {
                    factPath,
                    reason: "unverified",
                    message: "Proof or credentials must be agency-confirmed before this package",
                };
            }
            return {
                factPath,
                reason: "missing",
                message: "Confirmed proof or credentials are required",
            };
        case BUSINESS_GRAPH_PACKAGE_FACT_PATH.CONFIRMED_MEDIA:
            if (hasConfirmedMedia(graph)) {
                return null;
            }
            if (hasUnverifiedMedia(graph)) {
                return {
                    factPath,
                    reason: "unverified",
                    message: "Media assets must be agency-confirmed before this package",
                };
            }
            return {
                factPath,
                reason: "missing",
                message: "Confirmed media assets are required",
            };
        default: {
            const _exhaustive = factPath;
            return _exhaustive;
        }
    }
}
export function evaluatePackageRequiredFactGaps(graph, sitePackageId) {
    const paths = SITE_PACKAGE_REQUIRED_FACT_PATHS[sitePackageId];
    const gaps = [];
    for (const factPath of paths) {
        const gap = evaluateFactPath(graph, factPath);
        if (gap) {
            gaps.push(gap);
        }
    }
    return gaps;
}
export function packageRequiredFactHardBlockers(graph, sitePackageId) {
    return evaluatePackageRequiredFactGaps(graph, sitePackageId).map((gap) => `Package required field (${gap.factPath}): ${gap.message}`);
}
export function packageMeetsRequiredFactContract(graph, sitePackageId) {
    return evaluatePackageRequiredFactGaps(graph, sitePackageId).length === 0;
}
//# sourceMappingURL=business-graph-package-requirements.js.map